import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Shield, Heart, Award, CheckCircle, Zap } from 'lucide-react';
import { services, whyChooseUs, testimonials, clinicInfo } from '../data/mockData';

const Home = () => {
  const iconMap = {
    Star: Star,
    Shield: Shield,
    Heart: Heart,
    Award: Award,
    CheckCircle: CheckCircle,
    Zap: Zap
  };

  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--bg-primary)',
        padding: '0'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          minHeight: '600px'
        }} className="hero-grid">
          <div style={{
            padding: '80px 40px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <div className="container" style={{ padding: 0 }}>
              <h1 className="hero-large" style={{ marginBottom: '24px' }}>
                {clinicInfo.tagline}
              </h1>
              <p className="body-large" style={{ 
                marginBottom: '32px',
                color: 'var(--text-secondary)',
                maxWidth: '500px'
              }}>
                Experience premium dental care in Mansarovar, Jaipur with Dr. Parmila Mahala. 
                Advanced technology meets compassionate, painless treatment for your perfect smile.
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Link to="/book-appointment" className="btn-primary">
                  Book Appointment
                </Link>
                <a 
                  href={`tel:${clinicInfo.phone}`}
                  className="btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                >
                  Call Now
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
          <div style={{
            backgroundImage: 'url(https://images.pexels.com/photos/305567/pexels-photo-305567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
      </section>

      {/* Trust Bar */}
      <section style={{ 
        background: 'var(--bg-secondary)',
        padding: '48px 0',
        borderTop: '1px solid var(--border-light)',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '32px',
            textAlign: 'center'
          }}>
            <div>
              <div className="heading-2" style={{ marginBottom: '8px' }}>10+</div>
              <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Years Experience
              </div>
            </div>
            <div>
              <div className="heading-2" style={{ marginBottom: '8px' }}>5000+</div>
              <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Happy Patients
              </div>
            </div>
            <div>
              <div className="heading-2" style={{ marginBottom: '8px' }}>100%</div>
              <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Painless Care
              </div>
            </div>
            <div>
              <div className="heading-2" style={{ marginBottom: '8px' }}>Advanced</div>
              <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Technology
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              Our Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Comprehensive dental care with advanced techniques and compassionate service
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {services.slice(0, 6).map((service) => (
              <Link 
                key={service.id}
                to={`/services/${service.id}`}
                style={{ textDecoration: 'none' }}
              >
                <div className="product-card">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="product-card-image"
                  />
                  <h3 className="product-card-title">{service.title}</h3>
                  <p className="product-card-description">{service.shortDesc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/services" className="btn-secondary">
              View All Services
              <ArrowRight size={16} style={{ marginLeft: '8px' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{ background: 'var(--bg-secondary)' }} className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              Why Choose The Eternal Care
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Excellence in dental care through advanced technology and human touch
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {whyChooseUs.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div 
                  key={index}
                  style={{
                    padding: '32px',
                    background: 'var(--bg-primary)',
                    borderRadius: '0px',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <IconComponent size={24} color="var(--text-primary)" />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                    {item.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              What Our Patients Say
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Real experiences from real patients
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {testimonials.slice(0, 3).map((testimonial) => (
              <div 
                key={testimonial.id}
                style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="var(--text-primary)" color="var(--text-primary)" />
                  ))}
                </div>
                <p className="body-regular" style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="heading-3" style={{ marginBottom: '4px' }}>
                    {testimonial.name}
                  </div>
                  <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {testimonial.location}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/testimonials" className="btn-secondary">
              Read More Reviews
              <ArrowRight size={16} style={{ marginLeft: '8px' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'var(--interactive-base)',
        color: 'var(--bg-primary)',
        padding: '80px 0'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px', color: 'var(--bg-primary)' }}>
            Ready for a Painless Dental Experience?
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '32px', 
            maxWidth: '600px', 
            margin: '0 auto 32px',
            opacity: 0.9 
          }}>
            Book your appointment today and experience the difference of empathetic, 
            advanced dental care in Jaipur.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/book-appointment" 
              className="btn-primary"
              style={{
                background: 'var(--bg-primary)',
                color: 'var(--interactive-base)',
                border: '1px solid var(--bg-primary)'
              }}
            >
              Book Appointment
            </Link>
            <a 
              href={`https://wa.me/${clinicInfo.phone.replace(/[^0-9]/g, '')}?text=Hello! I would like to book an appointment.`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                background: 'transparent',
                color: 'var(--bg-primary)',
                border: '1px solid var(--bg-primary)'
              }}
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <style jsx>{`
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
          }
          
          .hero-grid > div:first-child {
            padding: 60px 24px !important;
          }
          
          .hero-grid > div:last-child {
            min-height: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
